import axios, { AxiosHeaders, type AxiosError, type InternalAxiosRequestConfig } from 'axios';

import type { AuthTokens } from '@/api/types';
import { clearAuthTokens, getAccessToken, getRefreshToken, setAuthTokens } from '@/auth/tokenStorage';

function resolveApiBaseUrl() {
  const configuredApiBase = import.meta.env.VITE_API_BASE?.trim();

  if (!configuredApiBase) {
    return '/api/';
  }

  const normalizedApiBase = configuredApiBase.replace(/\/+$/, '');

  if (normalizedApiBase.endsWith('/api')) {
    return `${normalizedApiBase}/`;
  }

  return `${normalizedApiBase}/api/`;
}

const API_BASE_URL = resolveApiBaseUrl();

const refreshClient = axios.create({
  baseURL: API_BASE_URL,
});

export const publicApiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

let refreshPromise: Promise<string | null> | null = null;

function applyAuthorizationHeader(
  config: InternalAxiosRequestConfig,
  accessToken: string,
) {
  const headers = AxiosHeaders.from(config.headers);
  headers.set('Authorization', `Bearer ${accessToken}`);
  config.headers = headers;
}

function isAuthRequest(url?: string) {
  return url?.includes('auth/login/') || url?.includes('auth/refresh/');
}

async function refreshAccessToken() {
  const refresh = getRefreshToken();

  if (!refresh) {
    clearAuthTokens();
    return null;
  }

  try {
    const { data } = await refreshClient.post<Pick<AuthTokens, 'access'>>('auth/refresh/', {
      refresh,
    });

    setAuthTokens({
      access: data.access,
      refresh,
    });

    return data.access;
  } catch {
    clearAuthTokens();
    return null;
  }
}

apiClient.interceptors.request.use((config) => {
  const accessToken = getAccessToken();

  if (accessToken) {
    applyAuthorizationHeader(config, accessToken);
  }

  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const requestConfig = error.config as (InternalAxiosRequestConfig & { _retry?: boolean }) | undefined;

    if (!requestConfig) {
      return Promise.reject(error);
    }

    if (error.response?.status !== 401 || requestConfig._retry || isAuthRequest(requestConfig.url)) {
      return Promise.reject(error);
    }

    requestConfig._retry = true;

    refreshPromise ??= refreshAccessToken();
    const nextAccessToken = await refreshPromise.finally(() => {
      refreshPromise = null;
    });

    if (!nextAccessToken) {
      return Promise.reject(error);
    }

    applyAuthorizationHeader(requestConfig, nextAccessToken);
    return apiClient(requestConfig);
  },
);